
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { InventoryItem } from '@/types/inventory';
import { useInventoryConnection } from './useInventoryConnection';
import { useWarehouseSelection } from './useWarehouseSelection';
import { useViewModeToggle, ViewMode } from './useViewModeToggle';
import { useInventorySearch } from './useInventorySearch';

// Change the re-export to use 'export type' instead of just 'export'
export type { ViewMode } from './useViewModeToggle';

export function useInventoryLiveSearch() {
  // Use our separated hooks
  const connection = useInventoryConnection();
  const warehouse = useWarehouseSelection();
  const viewMode = useViewModeToggle('list');
  const search = useInventorySearch({ 
    lastUpdate: connection.lastUpdate, 
    setLastUpdate: connection.setLastUpdate 
  });

  // Trigger search when search term or warehouse changes
  useEffect(() => {
    // Skip initial render or when both filters are empty
    if (search.debouncedSearch.length === 0 && warehouse.selectedWarehouse === null) {
      search.clearResults();
      return;
    }

    // Small timeout to prevent excessive API calls
    const timeoutId = setTimeout(async () => {
      try {
        await search.performSearch(search.debouncedSearch, warehouse.selectedWarehouse);
        
        // If no products found in specific warehouse, refresh warehouse list
        if (search.results.length === 0 && warehouse.selectedWarehouse !== null && search.debouncedSearch.length === 0) {
          warehouse.loadWarehouses();
        }
      } catch (error) {
        console.error('Search error:', error);
      }
    }, search.debouncedSearch.length < 2 ? 500 : 300);
    
    return () => clearTimeout(timeoutId);
  }, [search.debouncedSearch, warehouse.selectedWarehouse]);

  // Load warehouses on initial mount
  useEffect(() => {
    warehouse.loadWarehouses();
  }, []);

  return { 
    searchTerm: search.searchTerm, 
    setSearchTerm: search.setSearchTerm, 
    results: search.results, 
    isLoading: connection.isLoading || search.isLoading || warehouse.isLoading, 
    lastUpdate: connection.lastUpdate, 
    sampleProducts: connection.sampleProducts, 
    connectionVerified: connection.connectionVerified,
    availableWarehouses: warehouse.availableWarehouses,
    selectedWarehouse: warehouse.selectedWarehouse,
    setSelectedWarehouse: warehouse.selectWarehouse,
    reloadWarehouses: warehouse.refreshWarehouses,
    viewMode: viewMode.viewMode,
    toggleViewMode: viewMode.toggleViewMode
  };
}
